import React, { useState, useEffect } from 'react';
import { AutoComplete, Spin } from 'antd';
import axios from 'axios';
import GenericDialog from "@common/GenericDialog";

const AddStudentToClassDialog = ({ visible, selectedClass, onClose }) => {
    const [students, setStudents] = useState([]);
    const [filteredStudents, setFilteredStudents] = useState([]);
    const [selectedStudent, setSelectedStudent] = useState(null);
    const [searchValue, setSearchValue] = useState('');
    const [loading, setLoading] = useState(false);

    // Fetch users on component mount
    useEffect(() => {
        if (visible) {
            fetchUsers();
        }
    }, [visible]);

    // Axios function to get users
    const fetchUsers = async () => {
        setLoading(true);
        try {
            const response = await axios.get('/web/ui/sc/users');
            // Convert object of objects to array
            const usersArray = Object.values(response.data)
                .sort((a, b) => a.fullName.localeCompare(b.fullName, undefined, { sensitivity: 'base' }));
            setStudents(usersArray);
            setFilteredStudents(usersArray);
        } catch (error) {
            console.error('Error fetching users:', error);
            displayToastMessage('Failed to load students', null, 'error', undefined, 10000);
        } finally {
            setLoading(false);
        }
    };

    // Axios function to add user to class
    const addUserToClass = async (studentIds) => {
        try {
            await axios.post(`/web/ui/sc/addToClass?classId=${selectedClass?.classId}`, studentIds);
            return true;
        } catch (error) {
            console.error('Error adding user to class:', error);
            displayToastMessage('Failed to add student to class', null, 'error', undefined, 10000);
            return false;
        }
    };

    // Handle search functionality - now includes email search
    const handleSearch = (value) => {
        setSearchValue(value);
        if (!value) {
            setFilteredStudents(students);
            setSelectedStudent(null);
            return;
        }

        const filtered = students.filter(student =>
            student.fullName.toLowerCase().includes(value.toLowerCase()) ||
            student.username.toLowerCase().includes(value.toLowerCase()) ||
            student.email.toLowerCase().includes(value.toLowerCase())
        );
        setFilteredStudents(filtered);
    };

    // Handle selection
    const handleSelect = (value, option) => {
        const student = students.find(s => s.userId.toString() === value);
        setSelectedStudent(student);
        setSearchValue(student ? student.fullName : '');
    };

    const handleAdd = async () => {
        if (!selectedStudent) {
            return; // Button should be disabled, but just in case
        }

        const success = await addUserToClass([selectedStudent.userId]);
        if (success) {
            displayToastMessage(`${selectedStudent.fullName} added to ${selectedClass.className}`, null, 'success', undefined, 10000);
            handleClose();
        }
    };

    const handleClose = () => {
        setSelectedStudent(null);
        setSearchValue('');
        setFilteredStudents(students);
        onClose();
    };

    // Updated options to show both name and email
    const options = filteredStudents.map(student => ({
        value: student.userId.toString(),
        label: (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                <span style={{ fontWeight: '500' }}>{student.fullName}</span>
                <span style={{ fontSize: '12px', color: '#666', opacity: 0.8 }}>
                    {student.email}
                </span>
            </div>
        )
    }));

    return (
        <GenericDialog
            title={`Add user to ${selectedClass?.className}`}
            open={visible}
            dialogWidth={400}
            onClose={handleClose}
            buttons={[
                {
                    name: 'Cancel',
                    extraProps: { type: 'text' }
                },
                {
                    name: 'Add',
                    callback: handleAdd,
                    disabled: !selectedStudent,
                    extraProps: {
                        type: 'primary'
                    }
                }
            ]}
        >
            <div style={{ padding: '20px 0' }}>
                <label htmlFor="student-autocomplete" style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                    Find a student or teacher
                </label>
                <AutoComplete
                    id="student-autocomplete"
                    style={{ width: '100%' }}
                    options={options}
                    placeholder="Search by name, username, or email..."
                    value={searchValue}
                    onSearch={handleSearch}
                    onSelect={handleSelect}
                    notFoundContent={loading ? <Spin size="small" /> : 'No students found'}
                    filterOption={false}
                    onClear={() => {
                        setSelectedStudent(null);
                        setSearchValue('');
                        setFilteredStudents(students);
                    }}
                />
            </div>
        </GenericDialog>
    );
};

export default AddStudentToClassDialog;